import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Linking, Alert, Platform, ActivityIndicator, Modal, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { useFocusEffect } from '@react-navigation/native';
import pilotService, { openNavigationToMission } from '../services/pilotService';
import { getToken } from '../services/auth/authService';
import { API_URL } from '../utils/config';
import apiClient from '../utils/axiosConfig';

let WebView = null;
if (Platform.OS !== 'web') {
  WebView = require('react-native-webview').WebView;
}

const EPP_LABELS = {
  hearing_protection: 'Protección auditiva',
  gloves:             'Guantes',
  foot_protection:    'Protección pies',
  eye_protection:     'Protector ocular',
  simple_mask:        'Mascarilla simple',
  respirator:         'Respirador',
  full_suit:          'Traje completo',
  protective_apron:   'Delantal protector',
  face_shield:        'Careta facial',
  head_protection:    'Protección cabeza',
};

const STATUS_LABEL = {
  pending:     '⏳ Pendiente',
  assigned:    '⏳ Pendiente',
  in_progress: '✈️ En ejecución',
  completed:   '✅ Completada',
  cancelled:   '❌ Cancelada',
};

const buildMapHtml = (blocks) => {
  const features = blocks
    .filter(b => b.polygon)
    .map(b => ({
      type: 'Feature',
      geometry: JSON.parse(b.polygon),
      properties: { name: b.name, area: b.area },
    }));
  return buildMapHtmlFromFeatures(features);
};

const buildLocationMapHtml = (locationName, polygonGeojson) => {
  if (!polygonGeojson) return null;
  const features = [{
    type: 'Feature',
    geometry: typeof polygonGeojson === 'string' ? JSON.parse(polygonGeojson) : polygonGeojson,
    properties: { name: locationName || '', area: null },
  }];
  return buildMapHtmlFromFeatures(features);
};

function buildMapHtmlFromFeatures(features) {
  const geojson = JSON.stringify({ type: 'FeatureCollection', features });

  return `<!DOCTYPE html>
<html><head>
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1">
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" crossorigin=""/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js" crossorigin=""><\/script>
<style>
* { margin:0; padding:0; box-sizing:border-box; }
html, body, #map { height: 100%; width: 100%; }
.block-label {
  background: rgba(15,23,42,0.82);
  backdrop-filter: blur(4px);
  border: 1.5px solid rgba(250,204,21,0.7);
  border-radius: 20px;
  padding: 3px 10px;
  white-space: nowrap;
  pointer-events: none;
  text-align: center;
  transform: translate(-50%, -50%);
}
.block-label-name {
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.3px;
  display: block;
}
.block-label-area {
  font-size: 10px;
  color: rgba(250,204,21,0.9);
  display: block;
  margin-top: 1px;
}
#fullscreen-btn {
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 1000;
  background: rgba(15,23,42,0.82);
  backdrop-filter: blur(4px);
  border: 1.5px solid rgba(255,255,255,0.2);
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
}
#fullscreen-btn:hover { background: rgba(15,23,42,0.95); }
</style>
</head><body>
<div id="map"></div>
<button id="fullscreen-btn" title="Pantalla completa">
  <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
  </svg>
</button>
<script>
document.addEventListener('DOMContentLoaded', function() {
  var map = L.map('map', { zoomControl: true, attributionControl: false });
  L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
    maxZoom: 19,
    attribution: 'ESRI'
  }).addTo(map);
  var data = ${geojson};
  var layer = L.geoJSON(data, {
    style: { color: '#facc15', weight: 2.5, fillColor: '#facc15', fillOpacity: 0.15 },
    onEachFeature: function(f, l) {
      var areaStr = f.properties.area ? f.properties.area.toFixed(1) + ' ha' : '';
      var icon = L.divIcon({
        className: '',
        html: '<div class="block-label"><span class="block-label-name">' + f.properties.name + '<\/span>' + (areaStr ? '<span class="block-label-area">' + areaStr + '<\/span>' : '') + '<\/div>',
        iconSize: null,
        iconAnchor: [0, 0],
      });
      L.marker(l.getBounds().getCenter(), { icon: icon }).addTo(map);
    }
  }).addTo(map);
  map.fitBounds(layer.getBounds(), { padding: [20, 20] });

  // Fullscreen
  var isFullscreen = false;
  document.getElementById('fullscreen-btn').addEventListener('click', function() {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen && document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen && document.exitFullscreen();
    }
    isFullscreen = !isFullscreen;
    setTimeout(function() { map.invalidateSize(); }, 300);
  });
});
<\/script>
</body></html>`;
};

function buildVraTilesMapHtml(tilesUrl, classes, bounds, fieldBlocks) {
  const VRA_COLORS = [
    [220, 50,  50,  160],
    [230, 160, 30,  160],
    [240, 210, 30,  160],
    [100, 190, 60,  160],
    [30,  150, 50,  160],
    [20,  120, 80,  160],
    [10,   90, 60,  160],
  ];
  const colormap = classes.map((cls, i) => {
    const lo = i === 0 ? cls.ndvi_min - 0.001 : cls.ndvi_min;
    const hi = i === classes.length - 1 ? cls.ndvi_max + 0.001 : cls.ndvi_max;
    const color = VRA_COLORS[i] || VRA_COLORS[VRA_COLORS.length - 1];
    return [[lo, hi], color];
  });
  // Colormap sin doble-encoding — Leaflet no re-encodifica la URL base
  const colormapStr = JSON.stringify(colormap);
  const baseUrl = tilesUrl.split('?')[0];
  const existingParams = tilesUrl.split('?')[1] || '';
  const tilesWithColormap = baseUrl + '?' + 'colormap=' + colormapStr + (existingParams ? '&' + existingParams : '');
  const boundsArr = [[bounds.minLat, bounds.minLng], [bounds.maxLat, bounds.maxLng]];
  const blocksGeoJSON = JSON.stringify({
    type: 'FeatureCollection',
    features: (fieldBlocks || []).filter(b => b.polygon).map(b => ({
      type: 'Feature',
      geometry: typeof b.polygon === 'string' ? JSON.parse(b.polygon) : b.polygon,
      properties: { name: b.name },
    })),
  });
  const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  return `<!DOCTYPE html>
<html><head>
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1">
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" crossorigin=""/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js" crossorigin=""><\/script>
<style>
* { margin:0; padding:0; box-sizing:border-box; }
html, body, #map { height:100%; width:100%; }
#spinner { position:absolute; inset:0; background:rgba(15,23,42,0.6); display:flex; align-items:center; justify-content:center; z-index:9999; }
.spin { width:32px; height:32px; border:3px solid rgba(255,255,255,0.3); border-top-color:#fff; border-radius:50%; animation:spin 0.8s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }
.block-label { background:rgba(15,23,42,0.75); border:1.5px solid rgba(250,204,21,0.7); border-radius:20px; padding:2px 8px; white-space:nowrap; pointer-events:none; transform:translate(-50%,-50%); }
.block-label span { font-size:11px; font-weight:700; color:#fff; }
</style>
</head><body>
<div id="map"></div>
<div id="spinner"><div class="spin"></div></div>
<script>
document.addEventListener('DOMContentLoaded', function() {
  var map = L.map('map', { zoomControl: true, attributionControl: false });
  L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', { maxZoom: 20 }).addTo(map);

  var cogBounds = L.latLngBounds(
    [${bounds.minLat}, ${bounds.minLng}],
    [${bounds.maxLat}, ${bounds.maxLng}]
  );
  var tileUrlFinal = '${tilesWithColormap}';
  console.log('[MAP] tileUrl[:120]:', tileUrlFinal.substring(0,120));
  var vraTiles = L.tileLayer(tileUrlFinal, {
    opacity: 0.85, maxZoom: 20,
    bounds: cogBounds,
    errorTileUrl: '${TRANSPARENT_PIXEL}',
  });
  vraTiles.addTo(map);
  // Ocultar spinner cuando los tiles VRA terminan (o timeout de 4s)
  var vraDone = false;
  function hideSpinner() {
    if (vraDone) return; vraDone = true;
    var sp = document.getElementById('spinner');
    if (sp) sp.style.display = 'none';
  }
  vraTiles.once('load', hideSpinner);
  vraTiles.on('tileload', function() { setTimeout(hideSpinner, 200); });

  // Polígonos de cuarteles
  var blocks = ${blocksGeoJSON};
  if (blocks.features.length > 0) {
    L.geoJSON(blocks, {
      style: { color: '#facc15', weight: 2, fillOpacity: 0 },
      onEachFeature: function(f, l) {
        var icon = L.divIcon({ className: '', html: '<div class="block-label"><span>' + f.properties.name + '<\/span><\/div>', iconSize: null, iconAnchor: [0,0] });
        L.marker(l.getBounds().getCenter(), { icon: icon }).addTo(map);
      }
    }).addTo(map);
  }

  map.fitBounds(${JSON.stringify(boundsArr)}, { padding: [20, 20] });
  setTimeout(function() {
    var sp = document.getElementById('spinner');
    if (sp) sp.style.display = 'none';
  }, 5000);
});
<\/script>
</body></html>`;
}

const MapView = ({ html }) => {
  if (Platform.OS === 'web') {
    return (
      <iframe
        srcDoc={html}
        style={{ width: '100%', height: '100%', border: 'none' }}
        sandbox="allow-scripts allow-same-origin"
      />
    );
  }
  return (
    <WebView
      originWhitelist={['*']}
      source={{ html }}
      style={{ flex: 1 }}
      scrollEnabled={false}
      javaScriptEnabled={true}
      domStorageEnabled={true}
      mixedContentMode="always"
    />
  );
};

const WeatherSection = ({ weather, styles }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>CONDICIONES CLIMÁTICAS</Text>
    {weather?.today ? (
      <>
        <View style={[styles.row, { gap: 16, flexWrap: 'wrap', marginBottom: 8 }]}>
          <View style={styles.weatherItem}>
            <Ionicons name="thermometer-outline" size={16} color="#6b7280" />
            <Text style={styles.weatherText}>{weather.today.min}–{weather.today.max}°C</Text>
          </View>
          <View style={styles.weatherItem}>
            <Ionicons name="flag-outline" size={16} color="#6b7280" />
            <Text style={styles.weatherText}>{weather.today.wind} km/h</Text>
          </View>
          {weather.today.rain > 0 && (
            <View style={styles.weatherItem}>
              <Ionicons name="rainy-outline" size={16} color="#6b7280" />
              <Text style={styles.weatherText}>{weather.today.rain} mm</Text>
            </View>
          )}
        </View>
        {weather.forecast?.length > 0 && (
          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {weather.forecast.slice(0, 4).map((day, i) => (
              <View key={i} style={styles.forecastCard}>
                <Text style={styles.forecastDate}>
                  {new Date(day.date + 'T12:00:00').toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric' })}
                </Text>
                <Text style={styles.forecastTemp}>{day.min}–{day.max}°C</Text>
                {day.rain > 0 && <Text style={styles.forecastRain}>{day.rain} mm</Text>}
              </View>
            ))}
          </View>
        )}
      </>
    ) : (
      <Text style={[styles.sub, { fontStyle: 'italic' }]}>Cargando clima…</Text>
    )}
  </View>
);

export default function PilotMissionDetailScreen({ route, navigation }) {
  const initialItem = route.params?.item || route.params?.request;
  const readOnly = route.params?.readOnly || false;
  const { user } = useSelector(s => s.auth);
  const [item, setItem]           = useState(initialItem);
  const [weather, setWeather]     = useState(null);
  const [myAircraft, setMyAircraft] = useState([]);
  const [aircraftModal, setAircraftModal] = useState(false);
  const [settingAircraft, setSettingAircraft] = useState(false);
  const [aircraftError, setAircraftError] = useState(null);
  const [vraTiles, setVraTiles] = useState(null);
  const [pilotModal, setPilotModal] = useState(false);
  const [operatorPilots, setOperatorPilots] = useState([]);
  const [operatorFleet, setOperatorFleet] = useState([]);
  const [assigningPilot, setAssigningPilot] = useState(false);
  const [selectedPilotId, setSelectedPilotId] = useState(null);
  const [selectedAircraftIdForPilot, setSelectedAircraftIdForPilot] = useState(null);

  const isDroneRequest = item._type === 'mission_request';
  const canAssignPilot = isDroneRequest
    && item.status !== 'completed'
    && item.status !== 'cancelled'
    && user?.is_drone_operator;
  const canChangeAircraft = isDroneRequest
    && item.status !== 'completed'
    && item.status !== 'cancelled'
    && (user?.is_drone_operator || (user?.is_pilot && !user?.pilot_has_company));

  // Cargar VRA tiles al montar si es vra_ndvi
  useEffect(() => {
    const mrId = item?._raw_id;
    if (item?.rate_type === 'vra_ndvi' && mrId) {
      apiClient.get('/mission-requests/' + mrId + '/vra-tiles/')
        .then(res => {
          console.log('[VRA tiles] bounds:', res.data?.bounds, 'tiles_url[:80]:', res.data?.tiles_url?.substring(0,80), 'classes:', res.data?.prescription_classes?.length);
          console.log('[ITEM] prescription_classes:', item?.prescription_classes, 'rate_type:', item?.rate_type);
          setVraTiles(res.data);
        })
        .catch(e => { console.warn('[VRA tiles] error:', e); setVraTiles(null); });
    }
  }, [item?._raw_id, item?.rate_type]);

  useFocusEffect(useCallback(() => {
    if (!initialItem?._raw_id) return;
    pilotService.getAllPilotMissions()
      .then(missions => {
        const fresh = missions.find(m => m._raw_id === initialItem._raw_id && m._type === initialItem._type);
        if (fresh) setItem(fresh);
      })
      .catch(() => {});
    // Cargar clima para cualquier misión drone
    if (initialItem?.mr_type && initialItem?._mission_id) {
      pilotService.getMissionWeather(initialItem._mission_id)
        .then(data => { if (data) setWeather(data); })
        .catch(() => {});
    }
    // Cargar aeronaves del piloto
    pilotService.getMyAircraft().then(setMyAircraft).catch(() => {});
    // Cargar pilotos del operador si es admin
    if (user?.is_drone_operator) {
      Promise.all([
        apiClient.get('/operator-missions/'),
        apiClient.get('/operator-fleet/'),
      ]).then(([missionsResp, fleetResp]) => {
        setOperatorPilots(missionsResp.data?.pilots || []);
        setOperatorFleet(fleetResp.data || []);
      }).catch(() => {});
    }
  }, [initialItem?._raw_id]));

  const handleAssignPilot = async (pilotId, aircraftId) => {
    setAssigningPilot(true);
    try {
      const resp = await apiClient.post(`/operator-missions/${item._raw_id}/assign-pilot/`, {
        pilot_id: pilotId || null,
        aircraft_id: aircraftId || null,
      });
      setItem(prev => ({
        ...prev,
        pilot: resp.data?.pilot || null,
        aircraft: resp.data?.aircraft || null,
      }));
      setPilotModal(false);
    } catch (e) {
      Alert.alert('Error', e?.response?.data?.error || 'No se pudo asignar el piloto.');
    } finally {
      setAssigningPilot(false);
    }
  };

  const handleSetAircraft = async (aircraftId) => {
    setSettingAircraft(true);
    setAircraftError(null);
    try {
      const resp = await apiClient.post(
        `/mission-requests/${item._raw_id}/set-aircraft/`,
        { aircraft_id: aircraftId || null }
      );
      setItem(prev => ({ ...prev, aircraft: resp.data.aircraft }));
      setAircraftModal(false);
    } catch (e) {
      setAircraftError(e?.response?.data?.error || 'No se pudo cambiar la aeronave.');
    } finally {
      setSettingAircraft(false);
    }
  };

  if (!item) return null;

  const isDrone = item._type === 'mission_request';
  const isApplication = isDrone && item.mr_type === 'application';
  const isMultispectral = isDrone && item.mr_type === 'multispectral';

  const handleOpenPdf = async () => {
    try {
      const token = await getToken();
      const baseUrl = API_URL.replace('/api/v1', '');
      const url = `${baseUrl}/api/v1/orders/${item.order_id}/pdf/?token=${token}`;
      await Linking.openURL(url);
    } catch (e) {
      Alert.alert('Error', 'No se pudo abrir el PDF.');
    }
  };

  const handleNavigate = () => openNavigationToMission(item);

  const handleOpenKmz = async () => {
    try {
      const token = await getToken();
      const baseUrl = API_URL.replace('/api/v1', '');
      const url = `${baseUrl}/api/v1/mission-requests/${item._raw_id}/kmz/?token=${token}`;
      await Linking.openURL(url);
    } catch (e) {
      Alert.alert('Error', 'No se pudo descargar el KMZ.');
    }
  };

  return (
    <View style={{ flex: 1 }}>
      <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 100 }}>

        {/* Info general */}
        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={styles.title}>
              {isMultispectral
                ? `Misión #${item._mission_id}`
                : isApplication
                ? `Orden #${String(item.order_folio).padStart(4,'0')}  ·  Misión #${item._mission_id}`
                : item.mission_name || `Misión #${item.mission_sid || item._raw_id}`}
            </Text>
            <View style={[styles.badge, item.status === 'completed' ? styles.badgeDone : styles.badgeActive]}>
              <Text style={[styles.badgeText, item.status === 'completed' ? styles.badgeDoneText : styles.badgeActiveText]}>
                {STATUS_LABEL[item.status] || item.status}
              </Text>
            </View>
          </View>
          <Text style={styles.sub}>{item.location_name || item.enterprise_name}</Text>
          {isDrone && isApplication && (
            <Text style={styles.sub}>
              Cuarteles: {(item.field_blocks || []).map(b => b.name).join(', ')}
            </Text>
          )}
          {isMultispectral && item.objectives_list && item.objectives_list.length > 0 && (
            <Text style={styles.sub}>
              Índices: {item.objectives_list.join(' · ')}
            </Text>
          )}
          {isMultispectral && (item.field_blocks || []).length > 0 && (
            <Text style={styles.sub}>
              Cuarteles: {item.field_blocks.map(b => b.name).join(', ')}
            </Text>
          )}
          {isMultispectral && item.scheduled_date && (
            <Text style={styles.sub}>
              Fecha programada: {new Date(item.scheduled_date + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })}
            </Text>
          )}
          {!isDrone && item.mission_description ? (
            <Text style={styles.sub}>{item.mission_description}</Text>
          ) : null}
          {!isDrone && item.aircraft_name ? (
            <Text style={styles.sub}>Aeronave: {item.aircraft_name}</Text>
          ) : null}

          {/* Piloto asignable — solo para operador admin (abre modal unificado piloto+aeronave) */}
          {isDroneRequest && canAssignPilot && (
            <TouchableOpacity
              style={styles.aircraftRow}
              onPress={() => { setSelectedPilotId(null); setSelectedAircraftIdForPilot(null); setPilotModal(true); }}
              disabled={assigningPilot}
            >
              <Ionicons name="person-outline" size={15} color="#6b7280" />
              <Text style={styles.aircraftRowText}>
                {item.pilot?.name || 'Sin piloto asignado'}
              </Text>
              {assigningPilot
                ? <ActivityIndicator size="small" color="#6b7280" />
                : <Ionicons name="chevron-forward" size={14} color="#9ca3af" />}
            </TouchableOpacity>
          )}
          {isDroneRequest && canAssignPilot && (
            <TouchableOpacity
              style={styles.aircraftRow}
              onPress={() => { setAircraftError(null); setAircraftModal(true); }}
              disabled={settingAircraft}
            >
              <Ionicons name="airplane-outline" size={15} color="#6b7280" />
              <Text style={styles.aircraftRowText}>
                {item.aircraft?.name || 'Sin aeronave asignada'}
              </Text>
              {settingAircraft
                ? <ActivityIndicator size="small" color="#6b7280" />
                : <Ionicons name="chevron-forward" size={14} color="#9ca3af" />}
            </TouchableOpacity>
          )}

          {/* Aeronave asignable — piloto independiente o operador */}
          {isDroneRequest && canChangeAircraft && !canAssignPilot && (
            <TouchableOpacity
              style={styles.aircraftRow}
              onPress={() => { setAircraftError(null); setAircraftModal(true); }}
              disabled={settingAircraft}
            >
              <Ionicons name="airplane-outline" size={15} color="#6b7280" />
              <Text style={styles.aircraftRowText}>
                {item.aircraft?.name || 'Sin aeronave asignada'}
              </Text>
              {settingAircraft
                ? <ActivityIndicator size="small" color="#6b7280" />
                : <Ionicons name="chevron-forward" size={14} color="#9ca3af" />}
            </TouchableOpacity>
          )}
          {!isDrone && item.dgac_status ? (
            <Text style={styles.sub}>Permiso DGAC: {item.dgac_status}</Text>
          ) : null}
          {isMultispectral && ((item.field_blocks || []).some(b => b.polygon) || item.location_polygon) && (
            <View style={styles.btnRow}>
              {(item.field_blocks || []).some(b => b.polygon) && (
                <TouchableOpacity style={styles.pdfBtn} onPress={handleOpenKmz}>
                  <Ionicons name="navigate-outline" size={15} color="#2c3e50" />
                  <Text style={styles.pdfBtnText}>Descargar KMZ</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={styles.pdfBtn} onPress={handleNavigate}>
                <Ionicons name="location-outline" size={15} color="#2c3e50" />
                <Text style={styles.pdfBtnText}>Cómo llegar</Text>
              </TouchableOpacity>
            </View>
          )}
          {isApplication && (
            <View style={styles.btnRow}>
              <TouchableOpacity style={styles.pdfBtn} onPress={handleOpenPdf}>
                <Ionicons name="document-text-outline" size={15} color="#2c3e50" />
                <Text style={styles.pdfBtnText}>
                  {item.status === 'completed' ? 'Exportar PDF con registro' : 'Ver PDF de la orden'}
                </Text>
              </TouchableOpacity>
              {item.status !== 'completed' && (
                <TouchableOpacity style={styles.pdfBtn} onPress={handleOpenKmz}>
                  <Ionicons name="download-outline" size={15} color="#2c3e50" />
                  <Text style={styles.pdfBtnText}>Descargar prescripción</Text>
                </TouchableOpacity>
              )}
              {(item.field_blocks || []).some(b => b.polygon) && (
                <TouchableOpacity style={styles.pdfBtn} onPress={handleNavigate}>
                  <Ionicons name="location-outline" size={15} color="#2c3e50" />
                  <Text style={styles.pdfBtnText}>Cómo llegar</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>

        {/* Productos — solo para aplicaciones con dron */}
        {isApplication && (item.products || []).length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>📋 PRODUCTOS</Text>
            {item.products.map((p, i) => (
              <View key={i} style={styles.productCard}>
                <View style={styles.row}>
                  <Text style={styles.productName}>{p.trade_name}</Text>
                  <Text style={styles.productDose}>{p.dose_per_machine} {p.dose_unit}/ha</Text>
                </View>
                <Text style={styles.productSub}>{p.active_ingredient}</Text>
                {p.objective ? <Text style={styles.productSub}>Objetivo: {p.objective}</Text> : null}
                <View style={styles.row}>
                  {p.withholding_days != null && <Text style={styles.pill}>⏳ Carencia {p.withholding_days}d</Text>}
                  {p.reentry_hours    != null && <Text style={styles.pill}>🚷 Reingreso {p.reentry_hours}h</Text>}
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Zonas de aplicación — solo para dron */}
        {isApplication && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              💧 APLICACIÓN · {item.rate_type === 'uniform' ? 'Tasa uniforme' : item.rate_type === 'vra_ndvi' ? 'Tasa variable por zona' : 'Tasa variable'}
            </Text>
            {console.log('[RENDER] rate_type:', item.rate_type, 'pc length:', (item.prescription_classes||[]).length) || null}
            {item.rate_type === 'vra_ndvi'
              ? (item.prescription_classes || []).map((cls, i) => (
                  <View key={i} style={styles.zoneRow}>
                    <Text style={styles.zoneName}>Zona {i + 1}</Text>
                    <View style={styles.rowEnd}>
                      <Text style={{ fontSize: 11, color: '#9ca3af' }}>
                        NDVI {parseFloat(cls.ndvi_min).toFixed(2)} – {parseFloat(cls.ndvi_max).toFixed(2)}
                        {cls.area_ha != null ? ` · ${cls.area_ha} ha` : ''}
                      </Text>
                      <Text style={styles.zoneDose}>{cls.dose} L/ha</Text>
                    </View>
                  </View>
                ))
              : item.rate_type === 'uniform'
              ? (item.field_blocks || []).map((b, i) => (
                  <View key={i} style={styles.zoneRow}>
                    <Text style={styles.zoneName}>{b.name}</Text>
                    <View style={styles.rowEnd}>
                      {b.area != null && <Text style={styles.zoneArea}>{b.area} ha</Text>}
                      <View style={{ alignItems: 'flex-end' }}>
                        {b.area && (item.products || []).map((p, pi) => {
                          const total = (parseFloat(p.dose_per_machine) * parseFloat(b.area)).toFixed(1);
                          return (
                            <Text key={pi} style={styles.zoneDose}>
                              {p.trade_name}: {total} {p.dose_unit}
                            </Text>
                          );
                        })}
                        {item.wetting_per_ha && (
                          <Text style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                            {item.wetting_per_ha} L/ha
                          </Text>
                        )}
                      </View>
                    </View>
                  </View>
                ))
              : (item.prescription_map?.features || []).map((f, i) => {
                  const block = (item.field_blocks || []).find(b => b.id === f.properties?.field_block_id);
                  const area  = block?.area;
                  const dose  = parseFloat(f.properties?.dose);
                  const unit  = (f.properties?.unit || '').replace('/ha', '');
                  return (
                    <View key={i} style={styles.zoneRow}>
                      <Text style={styles.zoneName}>{f.properties?.zone_name || `Zona ${i+1}`}</Text>
                      <View style={styles.rowEnd}>
                        {area != null && <Text style={styles.zoneArea}>{area} ha</Text>}
                        <View style={{ alignItems: 'flex-end' }}>
                          {area != null && (item.products || []).map((p, pi) => {
                            const total = (parseFloat(p.dose_per_machine) * area).toFixed(1);
                            return (
                              <Text key={pi} style={styles.zoneDose}>
                                {p.trade_name}: {total} {p.dose_unit}
                              </Text>
                            );
                          })}
                          <Text style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                            {dose} {f.properties?.unit}
                          </Text>
                          {area == null && (
                            <Text style={styles.zoneDose}>{dose} {f.properties?.unit}</Text>
                          )}
                        </View>
                      </View>
                    </View>
                  );
                })
            }
          </View>
        )}

        {/* Mapa VRA — zonas NDVI coloreadas */}
        {isApplication && item.rate_type === 'vra_ndvi' && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>MAPA DE ZONAS VRA</Text>
            <View>
              <View style={styles.mapContainer}>
                <MapView html={vraTiles
                  ? buildVraTilesMapHtml(vraTiles.tiles_url, vraTiles.prescription_classes, vraTiles.bounds, item.field_blocks)
                  : buildMapHtml(item.field_blocks)
                } />
              </View>
              {!vraTiles && (
                <View style={{ position: 'absolute', inset: 0, top: 0, left: 0, right: 0, bottom: 0,
                  alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'rgba(15,23,42,0.5)', borderRadius: 12 }}>
                  <ActivityIndicator size="large" color="#facc15" />
                  <Text style={{ color: '#fff', fontSize: 12, marginTop: 8 }}>Cargando mapa de zonas...</Text>
                </View>
              )}
              {vraTiles && (
                <View style={{ marginTop: 8, gap: 4 }}>
                  {(vraTiles.prescription_classes || []).map((cls, i) => {
                    const COLORS = ['#dc3232','#e6a01e','#f0d21e','#64be3c','#1e961e','#147850','#0a5a3c'];
                    return (
                      <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <View style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: COLORS[i] || '#888' }} />
                        <Text style={{ fontSize: 12, color: '#374151', fontWeight: '600' }}>Zona {i+1}</Text>
                        <Text style={{ fontSize: 11, color: '#6b7280' }}>
                          {cls.dose} L/ha
                          {cls.area_ha != null ? ` · ${cls.area_ha} ha` : ''}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              )}
            </View>
          </View>
        )}

        {/* Mapa de cuarteles — para aplicaciones con polígonos (sin VRA) */}
        {isApplication && item.rate_type !== 'vra_ndvi' && (item.field_blocks || []).some(b => b.polygon) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>MAPA DE CUARTELES</Text>
            <View style={styles.mapContainer}>
              <MapView html={buildMapHtml(item.field_blocks)} />
            </View>
          </View>
        )}

        {/* Clima — para aplicaciones */}
        {isApplication && (
          <WeatherSection weather={weather} styles={styles} />
        )}

        {/* Mapa + Clima — para misiones multiespectrales */}
        {isMultispectral && (
          <>
            {/* Si hay cuarteles seleccionados, mostrar mapa de cuarteles; si no, polígono del campo */}
            {(item.field_blocks || []).some(b => b.polygon) ? (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>MAPA DE CUARTELES</Text>
                <View style={styles.mapContainer}>
                  <MapView html={buildMapHtml(item.field_blocks)} />
                </View>
              </View>
            ) : item.location_polygon ? (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>MAPA DEL CAMPO</Text>
                <View style={styles.mapContainer}>
                  <MapView html={buildLocationMapHtml(item.location_name, item.location_polygon)} />
                </View>
              </View>
            ) : null}
            <WeatherSection weather={weather} styles={styles} />
          </>
        )}

        {/* EPP — solo para dron */}
        {isApplication && item.epp && Object.entries(item.epp).some(([,v]) => v) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>🛡️ EPP REQUERIDO</Text>
            <View style={styles.eppGrid}>
              {Object.entries(item.epp).filter(([,v]) => v).map(([k], i) => (
                <View key={i} style={styles.eppItem}>
                  <Ionicons name="checkmark-circle" size={14} color="#16a34a" />
                  <Text style={styles.eppText}>{EPP_LABELS[k] || k}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Registro de ejecución — solo si está completada y tiene datos */}
        {isApplication && item.execution_records && item.execution_records.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>REGISTRO DE EJECUCIÓN</Text>

            {/* Info del equipo — igual en todos los registros */}
            {item.execution_records[0]?.passes[0] && (
              <>
                <View style={styles.zoneRow}>
                  <Text style={styles.zoneName}>Dron</Text>
                  <Text style={styles.zoneDose}>{item.execution_records[0].passes[0].drone_model || '—'}</Text>
                </View>
                {item.execution_records[0].passes[0].drone_serial ? (
                  <View style={styles.zoneRow}>
                    <Text style={styles.zoneName}>N° serie</Text>
                    <Text style={styles.zoneDose}>{item.execution_records[0].passes[0].drone_serial}</Text>
                  </View>
                ) : null}
              </>
            )}

            {/* Registros por cuartel */}
            {item.execution_records.map((blockRec, bi) => {
              const totalVol  = blockRec.passes.reduce((s, p) => s + (p.volume_applied_l || 0), 0);
              const totalArea = blockRec.passes.reduce((s, p) => s + (p.area_covered_ha || 0), 0);
              return (
                <View key={bi} style={[styles.productCard, { marginTop: 10 }]}>
                  <View style={[styles.row, { justifyContent: 'space-between', marginBottom: 8 }]}>
                    <Text style={styles.productName}>{blockRec.block_name}</Text>
                    <Text style={styles.productDose}>{totalVol.toFixed(1)} L · {totalArea.toFixed(1)} ha</Text>
                  </View>
                  {blockRec.passes.map((p, pi) => (
                    <View key={pi} style={{ paddingLeft: 8, borderLeftWidth: 2, borderLeftColor: '#e2e8f0', marginBottom: 6 }}>
                      <Text style={styles.productSub}>
                        {p.application_date ? new Date(p.application_date + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }) : ''}
                        {p.start_time ? `  ·  ${p.start_time}–${p.end_time || '?'}` : ''}
                      </Text>
                      <Text style={styles.productSub}>
                        {p.volume_applied_l != null ? `${p.volume_applied_l} L` : ''}
                        {p.area_covered_ha  != null ? `  ·  ${p.area_covered_ha} ha` : ''}
                        {p.equipment_cleaned ? '  ·  ✓ Equipos limpios' : ''}
                      </Text>
                    </View>
                  ))}
                </View>
              );
            })}

            {/* Totales generales */}
            {(() => {
              const allPasses = item.execution_records.flatMap(r => r.passes);
              const vol  = allPasses.reduce((s, p) => s + (p.volume_applied_l || 0), 0);
              const area = allPasses.reduce((s, p) => s + (p.area_covered_ha  || 0), 0);
              return (
                <View style={[styles.zoneRow, { borderBottomWidth: 0, marginTop: 8 }]}>
                  <Text style={[styles.zoneName, { fontWeight: '700' }]}>Total</Text>
                  <Text style={[styles.zoneDose, { fontWeight: '700' }]}>{vol.toFixed(1)} L · {area.toFixed(1)} ha</Text>
                </View>
              );
            })()}
          </View>
        )}
      </ScrollView>

      {/* Modal aeronave */}
      {/* Modal asignar piloto con aeronaves */}
      <Modal visible={pilotModal} transparent animationType="fade" onRequestClose={() => { setPilotModal(false); setSelectedPilotId(null); setSelectedAircraftIdForPilot(null); }}>
        <Pressable style={styles.modalOverlay} onPress={() => { setPilotModal(false); setSelectedPilotId(null); setSelectedAircraftIdForPilot(null); }}>
          <Pressable style={styles.modalBox} onPress={e => e.stopPropagation?.()}>
            <Text style={styles.modalTitle}>Asignar piloto</Text>
            <ScrollView style={{ maxHeight: 380 }}>
              <TouchableOpacity
                style={[styles.aircraftOption, styles.aircraftOptionFirst]}
                onPress={() => handleAssignPilot(null, null)}
              >
                <Ionicons name="person-remove-outline" size={15} color="#9ca3af" />
                <Text style={[styles.aircraftOptionText, { marginLeft: 8 }]}>Sin asignar</Text>
              </TouchableOpacity>
              {operatorPilots.map(p => {
                const isSelected = selectedPilotId === p.id;
                const isCurrent = item.pilot?.id === p.id;
                const pilotAircraft = operatorFleet.filter(a => (a.pilots || []).some(pp => pp.id === p.id));
                return (
                  <View key={p.id}>
                    <TouchableOpacity
                      style={[styles.aircraftOption, (isCurrent || isSelected) && { backgroundColor: '#f0fdf4' }]}
                      onPress={() => {
                        setSelectedPilotId(isSelected ? null : p.id);
                        setSelectedAircraftIdForPilot(null);
                      }}
                    >
                      <Ionicons name={isCurrent ? "checkmark-circle" : "person-outline"} size={15} color={isCurrent ? "#16a34a" : "#6b7280"} />
                      <Text style={[styles.aircraftOptionText, { marginLeft: 8, flex: 1 }, isCurrent && { color: '#16a34a', fontWeight: '700' }]}>{p.name}</Text>
                      {isCurrent && !isSelected && <Text style={{ fontSize: 11, color: '#16a34a' }}>Asignado</Text>}
                      {isSelected && <Ionicons name="chevron-down" size={14} color="#6b7280" />}
                    </TouchableOpacity>
                    {isSelected && (
                      <View style={{ backgroundColor: '#f8fafc', paddingHorizontal: 16, paddingBottom: 8 }}>
                        {pilotAircraft.length === 0
                          ? <Text style={{ fontSize: 12, color: '#9ca3af', paddingVertical: 8 }}>Sin aeronaves asignadas</Text>
                          : pilotAircraft.map(ac => {
                            const isAcSel = selectedAircraftIdForPilot === ac.id;
                            return (
                              <TouchableOpacity
                                key={ac.id}
                                style={[styles.aircraftOption, isAcSel && { backgroundColor: '#eff6ff' }]}
                                onPress={() => setSelectedAircraftIdForPilot(isAcSel ? null : ac.id)}
                              >
                                <Ionicons name="airplane-outline" size={14} color={isAcSel ? "#1d4ed8" : "#6b7280"} />
                                <Text style={[styles.aircraftOptionText, { marginLeft: 8, flex: 1 }, isAcSel && { color: '#1d4ed8', fontWeight: '700' }]}>{ac.display || ac.name}</Text>
                                {isAcSel && <Ionicons name="checkmark" size={14} color="#1d4ed8" />}
                              </TouchableOpacity>
                            );
                          })
                        }
                        <TouchableOpacity
                          style={{ marginTop: 8, padding: 12, backgroundColor: '#1d4ed8', borderRadius: 12, alignItems: 'center' }}
                          onPress={() => { handleAssignPilot(p.id, selectedAircraftIdForPilot); setSelectedPilotId(null); setSelectedAircraftIdForPilot(null); }}
                        >
                          <Text style={{ color: '#fff', fontWeight: '700', fontSize: 14 }}>
                            {selectedAircraftIdForPilot ? 'Confirmar asignación' : 'Asignar sin aeronave'}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                );
              })}
            </ScrollView>
            <TouchableOpacity style={styles.modalCancel} onPress={() => { setPilotModal(false); setSelectedPilotId(null); setSelectedAircraftIdForPilot(null); }}>
              <Text style={styles.modalCancelText}>Cancelar</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal visible={aircraftModal} transparent animationType="fade" onRequestClose={() => { setAircraftModal(false); setAircraftError(null); }}>
        <Pressable style={styles.modalOverlay} onPress={() => { setAircraftModal(false); setAircraftError(null); }}>
          <Pressable style={styles.modalBox} onPress={e => e.stopPropagation?.()}>
            <Text style={styles.modalTitle}>Seleccionar aeronave</Text>
            {aircraftError && (
              <View style={styles.aircraftErrorBox}>
                <Ionicons name="warning-outline" size={15} color="#dc2626" />
                <Text style={styles.aircraftErrorText}>{aircraftError}</Text>
              </View>
            )}
            <TouchableOpacity style={styles.aircraftOption} onPress={() => handleSetAircraft(null)}>
              <Ionicons name="close-circle-outline" size={18} color="#9ca3af" />
              <Text style={[styles.aircraftOptionText, { color: '#9ca3af' }]}>Sin aeronave</Text>
            </TouchableOpacity>
            {(canAssignPilot ? operatorFleet : myAircraft).map(ac => {
              const isCurrent = item.aircraft?.id === ac.id;
              return (
                <TouchableOpacity key={ac.id} style={[styles.aircraftOption, isCurrent && styles.aircraftOptionActive]} onPress={() => handleSetAircraft(ac.id)}>
                  <Ionicons name={isCurrent ? 'checkmark-circle' : 'airplane-outline'} size={18} color={isCurrent ? '#16a34a' : '#1d4ed8'} />
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.aircraftOptionText, isCurrent && { color: '#16a34a', fontWeight: '700' }]}>{ac.name || ac.display}</Text>
                    <Text style={styles.aircraftOptionSub}>{ac.brand} {ac.model}{ac.serial_number ? ` — S/N: ${ac.serial_number}` : ''}</Text>
                  </View>
                  {isCurrent && <Text style={styles.aircraftCurrentBadge}>Actual</Text>}
                </TouchableOpacity>
              );
            })}
            {(canAssignPilot ? operatorFleet : myAircraft).length === 0 && (
              <Text style={styles.aircraftEmpty}>{canAssignPilot ? 'Sin aeronaves en la flota' : 'No tienes aeronaves asignadas'}</Text>
            )}
            <TouchableOpacity style={styles.modalCancel} onPress={() => setAircraftModal(false)}>
              <Text style={styles.modalCancelText}>Cancelar</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Footer */}
      {!readOnly && isDrone && item.status !== 'completed' && (
        <View style={styles.footer}>
          {item.status === 'assigned' ? (
            <TouchableOpacity
              style={[styles.completeBtn, isMultispectral ? { backgroundColor: '#059669' } : { backgroundColor: '#1d4ed8' }]}
              onPress={async () => {
                try {
                  await pilotService.startMission(item._raw_id);
                  const missions = await pilotService.getAllPilotMissions();
                  const fresh = missions.find(m => m._raw_id === item._raw_id && m._type === item._type);
                  if (fresh) setItem(fresh);
                } catch (e) {
                  if (Platform.OS === 'web') alert(e?.response?.data?.error || 'Error al iniciar la misión.');
                  else Alert.alert('Error', e?.response?.data?.error || 'Error al iniciar la misión.');
                }
              }}
            >
              <Ionicons name="rocket-outline" size={16} color="#fff" style={{ marginRight: 6 }} />
              <Text style={styles.completeBtnText}>Iniciar misión</Text>
            </TouchableOpacity>
          ) : isMultispectral ? (
            <TouchableOpacity
              style={[styles.completeBtn, { backgroundColor: '#059669' }]}
              onPress={() => navigation.navigate('PilotMultispectralComplete', { item })}
            >
              <Ionicons name="cloud-upload-outline" size={16} color="#fff" style={{ marginRight: 6 }} />
              <Text style={styles.completeBtnText}>Entregar archivos TIFF</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={styles.completeBtn}
              onPress={() => navigation.navigate('PilotApplicationRecord', { item })}
            >
              <Ionicons name={item.execution_records?.length > 0 ? 'add-circle-outline' : 'clipboard-outline'} size={16} color="#fff" style={{ marginRight: 6 }} />
              <Text style={styles.completeBtnText}>
                {item.execution_records?.length > 0 ? 'Continuar registro' : 'Registrar aplicación'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container:       { flex: 1, backgroundColor: '#f5f7fa' },
  section:         { backgroundColor: '#fff', marginHorizontal: 12, marginTop: 12, borderRadius: 16, padding: 16 },
  sectionTitle:    { fontSize: 11, fontWeight: '700', color: '#9ca3af', letterSpacing: 0.8, marginBottom: 10 },
  title:           { fontSize: 18, fontWeight: '700', color: '#111827', flex: 1 },
  sub:             { fontSize: 13, color: '#6b7280', marginTop: 3 },
  aircraftRow:        { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 8, paddingVertical: 8, paddingHorizontal: 10, backgroundColor: '#f8fafc', borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0' },
  aircraftRowText:    { flex: 1, fontSize: 13, color: '#374151' },
  modalOverlay:       { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  modalBox:           { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24, paddingBottom: 36 },
  modalTitle:         { fontSize: 17, fontWeight: '700', color: '#111827', marginBottom: 16 },
  modalCancel:        { marginTop: 8, padding: 14, alignItems: 'center', backgroundColor: '#f3f4f6', borderRadius: 14 },
  modalCancelText:    { fontSize: 15, fontWeight: '600', color: '#6b7280' },
  aircraftOption:     { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12, paddingHorizontal: 8, borderRadius: 10, marginBottom: 4 },
  aircraftOptionActive: { backgroundColor: '#f0fdf4' },
  aircraftOptionText: { fontSize: 14, color: '#374151' },
  aircraftOptionSub:  { fontSize: 11, color: '#9ca3af', marginTop: 1 },
  aircraftCurrentBadge: { fontSize: 11, color: '#16a34a', fontWeight: '600' },
  aircraftEmpty:      { fontSize: 13, color: '#9ca3af', fontStyle: 'italic', paddingVertical: 8, textAlign: 'center' },
  aircraftErrorBox:   { flexDirection: 'row', alignItems: 'flex-start', gap: 6, backgroundColor: '#fef2f2', borderRadius: 8, padding: 10, marginBottom: 12, borderWidth: 1, borderColor: '#fecaca' },
  aircraftErrorText:  { flex: 1, fontSize: 13, color: '#dc2626', lineHeight: 18 },
  row:             { flexDirection: 'row', alignItems: 'center' },
  rowEnd:          { flexDirection: 'row', alignItems: 'center', gap: 8 },
  badge:           { borderRadius: 20, paddingHorizontal: 10, paddingVertical: 4 },
  badgeActive:     { backgroundColor: '#ede9fe' },
  badgeDone:       { backgroundColor: '#dcfce7' },
  badgeText:       { fontSize: 12, fontWeight: '600' },
  badgeActiveText: { color: '#7c3aed' },
  badgeDoneText:   { color: '#16a34a' },
  productCard:     { backgroundColor: '#f9fafb', borderRadius: 10, padding: 12, marginBottom: 8 },
  productName:     { fontSize: 14, fontWeight: '600', color: '#111827', flex: 1 },
  productDose:     { fontSize: 13, color: '#4b5563', fontWeight: '500' },
  productSub:      { fontSize: 12, color: '#6b7280', marginTop: 2 },
  pill:            { fontSize: 11, color: '#92400e', backgroundColor: '#fef3c7', borderRadius: 20, paddingHorizontal: 8, paddingVertical: 2, marginRight: 6, marginTop: 6 },
  zoneRow:         { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  zoneName:        { fontSize: 13, color: '#374151', flex: 1 },
  zoneArea:        { fontSize: 13, color: '#6b7280' },
  zoneDose:        { fontSize: 13, fontWeight: '600', color: '#1d4ed8' },
  eppGrid:         { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  eppItem:         { flexDirection: 'row', alignItems: 'center', gap: 4 },
  eppText:         { fontSize: 13, color: '#374151' },
  footer:          { padding: 16, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#f3f4f6' },
  completeBtn:     { backgroundColor: '#2c3e50', borderRadius: 14, padding: 16, alignItems: 'center' },
  completeBtnText: { color: '#fff', fontWeight: '700', fontSize: 15 },
  btnRow:          { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  pdfBtn:          { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#f3f4f6', borderRadius: 10 },
  pdfBtnText:      { fontSize: 13, fontWeight: '600', color: '#2c3e50' },
  mapContainer:    { height: 300, borderRadius: 10, overflow: 'hidden', marginTop: 4 },
  weatherItem:      { flexDirection: 'row', alignItems: 'center', gap: 6 },
  weatherText:      { fontSize: 14, color: '#374151', fontWeight: '500' },
  forecastCard:     { backgroundColor: '#f9fafb', borderRadius: 10, padding: 10, alignItems: 'center', minWidth: 72 },
  forecastDate:     { fontSize: 11, color: '#9ca3af', fontWeight: '600', textTransform: 'capitalize' },
  forecastTemp:     { fontSize: 13, color: '#374151', fontWeight: '600', marginTop: 2 },
  forecastRain:     { fontSize: 11, color: '#3b82f6', marginTop: 2 },
});
